import { useState, useRef, useCallback } from 'react';

/**
 * Custom hook for detecting swipe gestures
 * @param {Object} options - Configuration options
 * @param {Function} options.onSwipeLeft - Callback for left swipe
 * @param {Function} options.onSwipeRight - Callback for right swipe
 * @param {number} options.threshold - Minimum swipe distance (default: 50px)
 * @param {number} options.maxTime - Maximum swipe duration in ms (default: 300ms)
 */
export const useSwipe = ({ 
  onSwipeLeft, 
  onSwipeRight, 
  threshold = 50,
  maxTime = 300,
} = {}) => {
  const [swiping, setSwiping] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null); // 'left' | 'right' | null
  
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const isHorizontalSwipe = useRef(false);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    isHorizontalSwipe.current = false;
    setSwiping(true);
    setSwipeOffset(0);
    setSwipeDirection(null);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!swiping) return;

    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - touchStartX.current;
    const diffY = currentY - touchStartY.current;

    // Determine if it's a horizontal swipe on first significant movement
    if (!isHorizontalSwipe.current && (Math.abs(diffX) > 10 || Math.abs(diffY) > 10)) {
      isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
    }

    // Only track horizontal swipes
    if (isHorizontalSwipe.current) {
      e.preventDefault(); // Prevent scrolling during horizontal swipe
      
      // Limit the swipe offset with resistance
      const maxOffset = 120;
      const resistance = 0.5;
      let offset = diffX;
      
      if (Math.abs(offset) > maxOffset) {
        offset = offset > 0 
          ? maxOffset + (offset - maxOffset) * resistance
          : -maxOffset + (offset + maxOffset) * resistance;
      }
      
      setSwipeOffset(offset);
      setSwipeDirection(offset > threshold ? 'right' : offset < -threshold ? 'left' : null);
    }
  }, [swiping, threshold]);

  const handleTouchEnd = useCallback(() => {
    if (!swiping) return;

    const swipeDuration = Date.now() - touchStartTime.current;
    const isQuickSwipe = swipeDuration < maxTime;

    // Trigger callbacks if threshold met
    if (isQuickSwipe || Math.abs(swipeOffset) > threshold) {
      if (swipeOffset > threshold && onSwipeRight) {
        onSwipeRight();
      } else if (swipeOffset < -threshold && onSwipeLeft) {
        onSwipeLeft();
      }
    }

    // Reset state
    setSwiping(false);
    setSwipeOffset(0);
    setSwipeDirection(null);
  }, [swiping, swipeOffset, threshold, maxTime, onSwipeLeft, onSwipeRight]);

  const handlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onTouchCancel: handleTouchEnd,
  };

  return {
    handlers,
    swiping,
    swipeOffset,
    swipeDirection,
  };
};

export default useSwipe;
